import { browser } from '../base/lib-meet-hour';
import { showErrorNotification } from '../notifications';

import { UPDATE_DROPBOX_TOKEN } from './actionTypes';
import { _authorizeDropbox } from './functions';
import logger from './logger';

const DROPBOX_NOTIFICATION_ERROR_MAP = {
    DROPBOX_INTEGRATION_NOT_CONFIGURED: 'recording.dropboxIntegrationNotConfigured',
    DROPBOX_OAUTH_CANCELLED: 'recording.dropboxOAuthCancelled',
    DROPBOX_OAUTH_ERROR: 'recording.dropboxAuthorizeFailed',
    DROPBOX_INVALID_CREDENTIALS: 'recording.dropboxInvalidCredentials',
    DROPBOX_POPUP_BLOCKED: 'recording.dropboxPopupBlocked',
    DROPBOX_POPUP_CLOSED: 'recording.dropboxPopupClosed',
    DROPBOX_OAUTH_TIMEOUT: 'recording.dropboxOAuthTimedOut',
    DROPBOX_MISSING_AUTH_CODE: 'recording.dropboxAuthCodeMissing',
    DROPBOX_TOKEN_EXCHANGE_FAILED: 'recording.dropboxTokenExchangeFailed',
    DROPBOX_REFRESH_EXCHANGE_FAILED: 'recording.dropboxRefreshTokenFailed',
    DROPBOX_PROFILE_LOAD_FAILED: 'recording.dropboxProfileLoadFailed',
    DROPBOX_SPACE_USAGE_LOAD_FAILED: 'recording.dropboxSpaceUsageLoadFailed'
};

/**
 * Maps Dropbox auth/runtime errors to localized notification props.
 *
 * @param {Error|Object} error - The thrown error.
 * @returns {Object}
 */
function getDropboxErrorNotification(error: Object = {}) {
    const errorCode = error?.code || error?.message;
    const descriptionKey = DROPBOX_NOTIFICATION_ERROR_MAP[errorCode];
    const details = error?.details;

    if (descriptionKey) {
        const notification = {
            titleKey: 'recording.dropboxError',
            descriptionKey
        };

        if (details) {
            notification.descriptionArguments = { details };
        }

        return notification;
    }

    return {
        titleKey: 'recording.dropboxError',
        descriptionKey: 'recording.dropboxAuthorizeFailed'
    };
}

/**
 * Action to authorize the Meet Hour Recording app in dropbox.
 *
 * @returns {Function}
 */
export function authorizeDropbox() {
    return (dispatch: Function, getState: Function) => {
        const state = getState();
        const { locationURL } = state['features/base/connection'];
        const { dropbox = {} } = state['features/base/config'];

        // By default we use the static page on the main domain for redirection.
        // So we need to setup only one redirect URI in dropbox app
        // configuration (not multiple for all the tenants).
        // In case deployment is running in subfolder dropbox.redirectURI
        // can be configured.
        let redirectURI
            = dropbox.redirectURI || `${locationURL.origin}/static/oauth.html`;

        const isElectronApp = typeof browser !== 'undefined'
            && typeof browser.isElectron === 'function'
            && browser.isElectron();

        if (isElectronApp && typeof redirectURI === 'string') {
            const appUrlParam = `app_url=${encodeURIComponent('go.meethour.io://')}`;

            if (redirectURI.indexOf('app_url=') === -1) {
                redirectURI = `${redirectURI}${redirectURI.includes('?') ? '&' : '?'}${appUrlParam}`;
            }
        }

        _authorizeDropbox(dropbox.appKey, redirectURI)
            .then(
                ({ token, rToken, expireDate }) => {
                    dispatch(updateDropboxToken(token, rToken, expireDate));
                })
            .catch(error => {
                logger.log('Cannot authorize dropbox', error);
                dispatch(showErrorNotification(getDropboxErrorNotification(error)));
            });
    };
}

/**
 * Action to update the dropbox access token.
 *
 * @param {string} token - The new token.
 * @param {string} rToken - The refresh token.
 * @param {number} expireDate - The token expiration date as UNIX timestamp.
 * @returns {{
 *     type: UPDATE_DROPBOX_TOKEN,
 *     token: string,
 *     rToken: string,
 *     expireDate: number
 * }}
 */
export function updateDropboxToken(token: string, rToken: string, expireDate: number) {
    return {
        type: UPDATE_DROPBOX_TOKEN,
        token,
        rToken,
        expireDate
    };
}
